import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  InputAdornment,
  Checkbox,
  Chip,
  Button,
} from "@mui/material";
import {
  Close,
  Forward,
  Search as SearchIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useSocketContext } from "src/context/SocketContext";
import authConfig from "src/configs/auth";
import files from "src/utils/constants";
import { ObjectId } from "bson";

/**
 * ForwardDialog Component
 *
 * This component displays a dialog with a list of conversations
 * When a user selects a conversation, it forwards the message
 * with proper metadata including isForwarded, forwardedBy, and forwardedFrom
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to close the dialog
 * @param {Object} props.message - The message to be forwarded
 * @param {Object} props.socket - Socket instance for emitting events
 */
const ForwardDialog = ({ open, onClose, message, socket }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);

  // multiple selection state
  const [selectedConversations, setSelectedConversations] = useState([]);
  const [isMultipleSelectMode, setIsMultipleSelectMode] = useState(false);

  // get socket context and current user
  const { getConversations, addEventListener, sendMessage } =
    useSocketContext();
  const currentUser = useSelector((state) => state.auth.user);

  const storedUserId =
    typeof window !== "undefined"
      ? window.localStorage.getItem(authConfig.storageUserIdKeyName)
      : null;
  const currentUserId = currentUser?.userId ?? storedUserId;

  // fetch conversations when dialog opens
  useEffect(() => {
    if (open && getConversations) {
      setLoading(true);
      getConversations();
    }

    // reset selection state when dialog opens/closes
    if (!open) {
      setSelectedConversations([]);
      setIsMultipleSelectMode(false);
    }
  }, [open, getConversations]);

  // listen for conversation list updates
  useEffect(() => {
    if (!addEventListener) return;

    const unsubscribeConversationList = addEventListener(
      "conversationList",
      (data) => {
        const payload = Array.isArray(data)
          ? data
          : Array.isArray(data?.conversations)
          ? data.conversations
          : [];

        if (payload.length > 0) {
          // transform conversations to match the format we need
          const transformedConversations = payload
            .map((conversation) => {
              if (conversation.isGroup) {
                return {
                  id: conversation._id,
                  name:
                    conversation.groupName ||
                    conversation.subject ||
                    "group chat",
                  avatar: conversation.groupProfile || null,
                  status:
                    conversation.lastMessage?.message || "no messages yet",
                  isGroup: true,
                };
              } else {
                // find the opposite user (not current user)
                // Add defensive checks for userId existence
                const oppositeUser = conversation.users?.find(
                  (user) => user?.userId?._id && user.userId._id !== currentUserId
                );

                if (!oppositeUser?.userId) return null;

                return {
                  id: conversation._id,
                  name:
                    oppositeUser.userId.fullName ||
                    oppositeUser.userId.userName ||
                    "unknown user",
                  avatar: oppositeUser.userId.image?.profilePic || null,
                  status:
                    conversation.lastMessage?.message || "no messages yet",
                  isGroup: false,
                  oppositeUserId: oppositeUser.userId._id,
                };
              }
            })
            .filter(Boolean); // remove null entries

          setConversations(transformedConversations);
          setLoading(false);
        }
      }
    );

    return () => {
      unsubscribeConversationList();
    };
  }, [addEventListener, currentUserId]);

  // handle forward to conversation
  const handleForwardToConversation = (conversation) => {
    // guard: require message and sendMessage func
    if (!message || !sendMessage) {
      return;
    }

    // log original message for observability

    // compute original sender for forwardedFrom
    const originalSenderId =
      message.senderId || message.sender?._id || message.userId || null;

    // generate a new valid objectId for the forwarded message _id
    const newMessageId = new ObjectId().toString();

    // build payload aligned with regular send message payload
    // includes required forwarded metadata per backend schema
    const forwardPayload = {
      // core identifiers
      _id: newMessageId,
      senderId: currentUserId,
      conversationId: conversation.id,
      // content
      type:
        message.type ||
        (Array.isArray(message.attachments) && message.attachments[0]?.type) ||
        "text",
      message: message.text || "",
      attachments: Array.isArray(message.attachments)
        ? message.attachments
        : [],
      // message relations
      mentions: [],
      reactions: [],
      replyTo: null,
      repliedBy: [],
      // forwarded metadata
      isForwarded: true,
      forwardedFrom: originalSenderId, // original sender user id
      forwardedBy: [
        {
          userId: currentUserId,
          timestamp: new Date().toISOString(),
        },
      ],
      // lifecycle flags
      isDeletedForMe: [],
      isDeletedForEveryone: false,
      isEdited: false,
      editedBy: [],
      deliveredTo: [],
      readBy: [],
      // optional location data
      location: message.location || null,
      latLong: message.latLong || null,
    };

    try {
      // use the same send flow as normal messages so backend treats it identically
      const success = sendMessage(forwardPayload);
      if (success) {
      } else {
      }
    } catch (error) {}

    // close dialog
    onClose();
    setSearchQuery("");
  };

  // filter conversations based on search query
  const filteredConversations = conversations.filter((conv) => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      (conv.name && conv.name.toLowerCase().includes(searchLower)) ||
      (conv.status && conv.status.toLowerCase().includes(searchLower))
    );
  });

  // multiple selection handlers
  const handleToggleSelectionMode = () => {
    setIsMultipleSelectMode(!isMultipleSelectMode);
    if (!isMultipleSelectMode) {
      setSelectedConversations([]);
    }
  };

  const handleConversationSelect = (conversation) => {
    if (isMultipleSelectMode) {
      setSelectedConversations((prev) => {
        const isSelected = prev.some(
          (selected) => selected.id === conversation.id
        );

        if (isSelected) {
          const filtered = prev.filter(
            (selected) => selected.id !== conversation.id
          );

          return filtered;
        } else {
          const updated = [...prev, conversation];

          return updated;
        }
      });
    } else {
      handleForwardToConversation(conversation);
    }
  };

  const handleSelectAll = () => {
    if (selectedConversations.length === filteredConversations.length) {
      setSelectedConversations([]);
    } else {
      setSelectedConversations([...filteredConversations]);
    }
  };

  const handleForwardToMultiple = () => {
    // if (!socket || !message || selectedConversations.length === 0) {
    //   console.error("[ERROR] Multiple forward failed:", {
    //     socket: !!socket,
    //     message: !!message,
    //     selectedCount: selectedConversations.length
    //   });
    //   return;
    // }

    // Log the original message object for multiple forwarding

    let successCount = 0;
    let errorCount = 0;

    selectedConversations.forEach((conversation, index) => {
      // compute original sender once
      const originalSenderId =
        message.senderId || message.sender?._id || message.userId || null;

      // generate a new _id for each forwarded message
      const newMessageId = new ObjectId().toString();

      // build payload aligned with regular send message payload
      const forwardPayload = {
        _id: newMessageId,
        senderId: currentUserId,
        conversationId: conversation.id,
        type:
          message.type ||
          (Array.isArray(message.attachments) &&
            message.attachments[0]?.type) ||
          "text",
        message: message.text || "",
        attachments: Array.isArray(message.attachments)
          ? message.attachments
          : [],
        mentions: [],
        reactions: [],
        replyTo: null,
        repliedBy: [],
        isForwarded: true,
        forwardedFrom: originalSenderId,
        forwardedBy: [
          {
            userId: currentUserId,
            timestamp: new Date().toISOString(),
          },
        ],
        isDeletedForMe: [],
        isDeletedForEveryone: false,
        isEdited: false,
        editedBy: [],
        deliveredTo: [],
        readBy: [],
        location: message.location || null,
        latLong: message.latLong || null,
      };

      try {
        const success = sendMessage(forwardPayload);
        if (success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        errorCount++;
      }
    });

    // Log final results

    setSelectedConversations([]);
    setIsMultipleSelectMode(false);
    onClose();
    setSearchQuery("");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={false}
      disableBackdropClick={false}
      keepMounted={false}
      PaperProps={{
        elevation: 8,
        sx: {
          borderRadius: 3,
          maxHeight: "600px",
          backgroundColor: "#ffffff",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#374151" }}>
            forward message
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <IconButton
              size="small"
              onClick={handleToggleSelectionMode}
              sx={{
                padding: 1,
                backgroundColor: isMultipleSelectMode
                  ? "primary.main"
                  : "transparent",
                color: isMultipleSelectMode
                  ? "primary.contrastText"
                  : "#6B7280",
                "&:hover": {
                  backgroundColor: isMultipleSelectMode
                    ? "primary.dark"
                    : "action.hover",
                },
              }}
            >
              <CheckBoxIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: "#6B7280" }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 0 }}>
        {/* search bar */}
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="search conversations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              backgroundColor: "#f9fafb",
            },
          }}
        />

        {/* selection controls */}
        {isMultipleSelectMode && (
          <Box
            sx={{
              mb: 2,
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Chip
              label={`select all (${selectedConversations.length}/${filteredConversations.length})`}
              onClick={handleSelectAll}
              color={
                selectedConversations.length === filteredConversations.length
                  ? "primary"
                  : "default"
              }
              variant={
                selectedConversations.length === filteredConversations.length
                  ? "filled"
                  : "outlined"
              }
              sx={{ cursor: "pointer" }}
            />
            {selectedConversations.length > 0 && (
              <Chip
                label={`forward to ${
                  selectedConversations.length
                } conversation${selectedConversations.length > 1 ? "s" : ""}`}
                onClick={handleForwardToMultiple}
                color="success"
                variant="filled"
                sx={{ cursor: "pointer" }}
              />
            )}
          </Box>
        )}

        {/* conversations list */}
        <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
          {loading ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                loading conversations...
              </Typography>
            </Box>
          ) : filteredConversations.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                {searchQuery
                  ? "no conversations found"
                  : "no conversations available"}
              </Typography>
            </Box>
          ) : (
            filteredConversations.map((conversation) => (
              <ListItem
                key={conversation.id}
                onClick={() => handleConversationSelect(conversation)}
                sx={{
                  cursor: "pointer",
                  borderRadius: 2,
                  mb: 1,
                  position: "relative",
                  backgroundColor:
                    isMultipleSelectMode &&
                    selectedConversations.some(
                      (selected) => selected.id === conversation.id
                    )
                      ? "primary.light"
                      : "transparent",
                  border:
                    isMultipleSelectMode &&
                    selectedConversations.some(
                      (selected) => selected.id === conversation.id
                    )
                      ? "2px solid"
                      : "none",
                  borderColor: "primary.main",
                  "&:hover": {
                    backgroundColor:
                      isMultipleSelectMode &&
                      selectedConversations.some(
                        (selected) => selected.id === conversation.id
                      )
                        ? "primary.light"
                        : "#f3f4f6",
                  },
                }}
              >
                {/* Checkbox for multiple selection */}
                {isMultipleSelectMode && (
                  <Checkbox
                    checked={selectedConversations.some(
                      (selected) => selected.id === conversation.id
                    )}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleConversationSelect(conversation);
                    }}
                    sx={{
                      mr: 1,
                      "& .MuiSvgIcon-root": {
                        fontSize: "1.2rem",
                      },
                    }}
                  />
                )}

                <ListItemIcon sx={{ ml: isMultipleSelectMode ? 0 : 0 }}>
                  <Box
                    sx={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      backgroundColor: conversation.isGroup
                        ? "primary.light"
                        : "#e5e7eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.2rem",
                      overflow: "hidden",
                      border: "2px solid",
                      borderColor: conversation.isGroup
                        ? "primary.main"
                        : "transparent",
                      boxShadow: conversation.isGroup
                        ? "0 2px 4px rgba(0,0,0,0.1)"
                        : "none",
                    }}
                  >
                    {conversation.avatar ? (
                      <img
                        src={files(conversation.avatar, "image")}
                        alt={conversation.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "50%",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: conversation.isGroup
                            ? "primary.main"
                            : "grey.300",
                          color: conversation.isGroup
                            ? "primary.contrastText"
                            : "text.primary",
                          fontSize: "1rem",
                          fontWeight: "bold",
                        }}
                      >
                        {conversation.isGroup
                          ? "G"
                          : conversation.name
                          ? conversation.name.charAt(0).toUpperCase()
                          : "?"}
                      </Box>
                    )}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#374151" }}
                    >
                      {conversation.name}
                      {conversation.isGroup && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            ml: 1,
                            color: "#6B7280",
                            backgroundColor: "#f3f4f6",
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                          }}
                        >
                          group
                        </Typography>
                      )}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" sx={{ color: "#6B7280" }}>
                      {conversation.status}
                    </Typography>
                  }
                />
                {!isMultipleSelectMode && (
                  <Forward fontSize="small" sx={{ color: "#6B7280" }} />
                )}
              </ListItem>
            ))
          )}
        </Box>

        {/* message preview */}
        {message && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: "#f9fafb",
              borderRadius: 2,
              border: "1px solid #e5e7eb",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#6B7280", mb: 1, display: "block" }}
            >
              forwarding:
            </Typography>
            <Typography variant="body2" sx={{ color: "#374151" }}>
              {message.text || "message content"}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForwardDialog;
